// Executors: the connections agents think with. A connection is a named
// login for one kind of executor (Claude Code, a ChatGPT subscription, or
// an OpenAI-compatible API); a workspace can have several of each — two
// Claude Code accounts, an OpenRouter key and a company gateway — and every
// agent picks one. Existing Codex (`codex_cli`) connections keep listing,
// editing and working; it just isn't offered when adding a new one.
//
// Logins are stored encrypted and never shown again; an API is asked for its
// models before it's saved, so a wrong address or key shows up here.

import {
  useId,
  useMemo,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CLAUDE_CLI_DEFAULT_MODEL_TIERS,
  MODEL_TIERS,
  type ConnectionModelTiers,
  type ModelTier,
} from '@goblic/shared'
import {
  ApiError,
  CONNECTION_KIND_LABEL,
  connectionsApi,
  type ConnectionKind,
  type ExecutorConnection,
} from '../lib/api-client'
import { useWorkspaceId } from '../lib/auth'
import { ExhaustionBadge } from '../components/exhaustion-badge'
import { TOKEN_HELP } from '../lib/executor-login'
import {
  OPENAI_COMPATIBLE_PROVIDERS,
  type OpenAiCompatibleProvider,
} from '../lib/openai-compatible-providers'
import { useDialogs } from '../components/dialogs'
import {
  Dialog,
  EmptyState,
  ErrorNote,
  Field,
  LoadingRows,
  Notice,
  PageHeader,
} from '../components/ui'
import { formatRelative } from '../lib/format'

/** Offered when adding a connection, in this order. `codex_cli` isn't
 * offered here any more — a ChatGPT subscription runs through `pi_login`
 * instead — but existing `codex_cli` connections aren't affected. */
const KINDS: { kind: ConnectionKind; text: string }[] = [
  {
    kind: 'claude_cli',
    text: 'A Claude subscription (claude setup-token) or an Anthropic API key.',
  },
  {
    kind: 'pi_login',
    text: 'Sign in with your ChatGPT account. Only one login per connection; don’t share it between people.',
  },
  {
    kind: 'openai_compatible',
    text: 'Any API that speaks OpenAI’s format: OpenRouter, a company gateway, vLLM.',
  },
]

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

function hostOf(url: string | null): string | null {
  if (!url) return null
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

export function ExecutorsPage() {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const [dialog, setDialog] = useState<
    { mode: 'add' } | { mode: 'edit'; connection: ExecutorConnection } | null
  >(null)

  const connectionsQuery = useQuery({
    queryKey: ['connections', workspaceId],
    queryFn: () => connectionsApi.list(workspaceId),
    enabled: workspaceId !== '',
  })
  const connections = connectionsQuery.data?.items ?? []

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['connections', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['executor-models'] }),
      queryClient.invalidateQueries({ queryKey: ['credentials', workspaceId] }),
    ])

  const remove = useMutation({
    mutationFn: (id: string) => connectionsApi.remove(id, workspaceId),
    onSuccess: refresh,
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t remove the connection',
        message: errorText(error, ''),
      }),
  })

  async function handleRemove(connection: ExecutorConnection): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Remove ${connection.name}?`,
      message:
        connection.agentCount > 0
          ? `${connection.agentCount === 1 ? 'One agent uses' : `${connection.agentCount} agents use`} it. They can’t run until they get another connection.`
          : 'The login is revoked. No agent uses it.',
      confirmLabel: 'Remove',
    })
    if (ok) remove.mutate(connection.id)
  }

  return (
    <div>
      <PageHeader
        title="Executors"
        description="The logins your agents think with. Add as many as you need, such as two Claude Code accounts or several APIs; every agent picks one."
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => setDialog({ mode: 'add' })}
          >
            Add connection
          </button>
        }
      />

      <div className="panel overflow-x-auto">
        {connectionsQuery.isLoading ? (
          <LoadingRows />
        ) : connectionsQuery.isError ? (
          <ErrorNote error={connectionsQuery.error} what="connections" />
        ) : connections.length === 0 ? (
          <EmptyState
            title="No connections yet"
            art="setup"
            action={
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDialog({ mode: 'add' })}
              >
                Add a connection
              </button>
            }
          >
            Agents need a Claude Code, ChatGPT or API login before they can run.
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Connection</th>
                <th>Kind</th>
                <th>Used by</th>
                <th>Added</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {connections.map((connection) => (
                <tr key={connection.id}>
                  <td>
                    <span className="block font-medium text-ink">{connection.name}</span>
                    <ExhaustionBadge
                      exhaustion={connection}
                      connectionId={connection.id}
                    />
                  </td>
                  <td>
                    <span className="block">
                      {CONNECTION_KIND_LABEL[connection.kind]}
                    </span>
                    {connection.endpoint ? (
                      <span
                        className="block font-mono text-sm text-ink-3"
                        title={connection.endpoint}
                      >
                        {hostOf(connection.endpoint)}
                      </span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap">
                    {connection.agentCount === 0
                      ? 'No agents'
                      : connection.agentCount === 1
                        ? '1 agent'
                        : `${connection.agentCount} agents`}
                  </td>
                  <td className="whitespace-nowrap">
                    {formatRelative(connection.createdAt)}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        className="btn-ghost btn-sm"
                        onClick={() => setDialog({ mode: 'edit', connection })}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-ghost btn-sm text-danger"
                        onClick={() => void handleRemove(connection)}
                        disabled={remove.isPending}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-sm text-ink-3">
        Gemini CLI, OpenHands and local model runners can’t run yet. An
        OpenAI-compatible API covers most other providers.
      </p>

      <Dialog
        open={dialog != null}
        onClose={() => setDialog(null)}
        title={
          dialog?.mode === 'edit'
            ? `Edit ${dialog.connection.name}`
            : 'Add a connection'
        }
        description="The login is stored encrypted and never shown again."
        art="/goblins/dialog-connect.webp"
        size="md"
      >
        {dialog ? (
          <ConnectionForm
            key={dialog.mode === 'edit' ? dialog.connection.id : 'add'}
            workspaceId={workspaceId}
            existing={dialog.mode === 'edit' ? dialog.connection : null}
            onDone={() => {
              setDialog(null)
              void refresh()
            }}
          />
        ) : null}
        {dialog?.mode === 'edit' ? (
          <ModelTiersBlock
            key={dialog.connection.id}
            workspaceId={workspaceId}
            connection={dialog.connection}
            onSaved={() => void refresh()}
          />
        ) : null}
      </Dialog>
    </div>
  )
}

/** One row in the provider combobox: "Custom" or a known provider. */
const PROVIDER_OPTIONS: { id: string; name: string }[] = [
  { id: 'custom', name: 'Custom' },
  ...OPENAI_COMPATIBLE_PROVIDERS.map((p) => ({ id: p.id, name: p.name })).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  ),
]

/**
 * A searchable combobox for the OpenAI-compatible provider field — type to
 * filter, arrow keys/Enter/Escape to pick, same behaviour as
 * components/model-picker.tsx's suggestion list. Picking a provider other
 * than "Custom" fills the hidden base URL (handled by the caller).
 */
function ProviderPicker({
  id,
  providerId,
  onSelect,
}: {
  id: string
  /** Current selection: `'custom'` or a provider's `id`. */
  providerId: string
  onSelect: (provider: OpenAiCompatibleProvider | null) => void
}) {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [query, setQuery] = useState<string | null>(null)

  const selected =
    PROVIDER_OPTIONS.find((o) => o.id === providerId) ?? PROVIDER_OPTIONS[0]!
  const rows = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase()
    return q
      ? PROVIDER_OPTIONS.filter((o) => o.name.toLowerCase().includes(q))
      : PROVIDER_OPTIONS
  }, [query])

  function choose(option: { id: string; name: string }): void {
    onSelect(
      option.id === 'custom'
        ? null
        : OPENAI_COMPATIBLE_PROVIDERS.find((p) => p.id === option.id) ?? null
    )
    setOpen(false)
    setQuery(null)
    setActive(-1)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        setActive(0)
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((i) => (i + step + rows.length) % rows.length)
    } else if (event.key === 'Enter' && open) {
      event.preventDefault()
      const row = rows[active]
      if (row) choose(row)
      else setOpen(false)
    } else if (event.key === 'Escape' && open) {
      event.preventDefault()
      setOpen(false)
      setQuery(null)
    }
  }

  const activeId = open && active >= 0 ? `${listId}-${active}` : undefined

  return (
    <div className="relative">
      <input
        id={id}
        className="field-input"
        value={query ?? selected.name}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        onChange={(event) => {
          setQuery(event.target.value)
          setActive(-1)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false)
          setQuery(null)
        }}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-control border border-line bg-surface py-1 shadow-overlay"
        >
          {rows.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-3">No match.</li>
          ) : null}
          {rows.map((option, index) => (
            <li key={option.id} role="presentation">
              <div
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.id === providerId}
                onMouseDown={(event) => {
                  event.preventDefault()
                  choose(option)
                }}
                onMouseEnter={() => setActive(index)}
                className={`cursor-pointer px-3 py-1.5 text-base text-ink ${
                  index === active ? 'bg-sunken' : ''
                }`}
              >
                {option.name}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * Add a connection (kind, name, login), or edit one: rename it, or replace
 * its login. When editing, empty login fields keep the stored login.
 */
export function ConnectionForm({
  workspaceId,
  existing,
  onDone,
}: {
  workspaceId: string
  existing: ExecutorConnection | null
  onDone: () => void
}) {
  const [kind, setKind] = useState<ConnectionKind>(
    existing?.kind ?? 'claude_cli'
  )
  const [name, setName] = useState(existing?.name ?? '')
  const [token, setToken] = useState('')
  const [baseUrl, setBaseUrl] = useState(existing?.endpoint ?? '')
  const [apiKey, setApiKey] = useState('')
  // The provider combobox: a known provider's id, or 'custom'. Preselect
  // whichever provider's base URL matches an existing connection's stored
  // endpoint; otherwise Custom (new connection, or an address we don't know).
  const [providerId, setProviderId] = useState<string>(() => {
    const match = OPENAI_COMPATIBLE_PROVIDERS.find(
      (p) => p.baseUrl === existing?.endpoint
    )
    return match?.id ?? 'custom'
  })
  const [piLoginFlow, setPiLoginFlow] = useState<{ url: string; state: string } | null>(null)
  const [piLoginRedirectUrl, setPiLoginRedirectUrl] = useState('')
  const isApi = kind === 'openai_compatible'
  const isPiLoginOAuth = kind === 'pi_login'
  const help = TOKEN_HELP[kind]
  const selectedProvider =
    providerId === 'custom'
      ? null
      : (OPENAI_COMPATIBLE_PROVIDERS.find((p) => p.id === providerId) ?? null)

  function selectProvider(provider: OpenAiCompatibleProvider | null): void {
    if (!provider) {
      setProviderId('custom')
      // Clear a base URL that was auto-filled; keep one the user typed.
      if (OPENAI_COMPATIBLE_PROVIDERS.some((p) => p.baseUrl === baseUrl))
        setBaseUrl('')
      return
    }
    setProviderId(provider.id)
    setBaseUrl(provider.baseUrl)
  }

  const save = useMutation({
    mutationFn: () => {
      const login = isApi
        ? {
            baseUrl: baseUrl.trim(),
            ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
          }
        : token.trim()
          ? { token: token.trim() }
          : {}
      const defaultName =
        isApi && selectedProvider ? selectedProvider.name : CONNECTION_KIND_LABEL[kind]
      return existing
        ? connectionsApi.update(existing.id, {
            workspaceId,
            name: name.trim(),
            ...login,
          })
        : connectionsApi.create({
            workspaceId,
            kind,
            name: name.trim() || defaultName,
            ...login,
          })
    },
    onSuccess: onDone,
  })

  const startPiLogin = useMutation({
    mutationFn: () => connectionsApi.piLoginStart(workspaceId),
    onSuccess: (flow) => {
      setPiLoginFlow(flow)
      setPiLoginRedirectUrl('')
    },
  })

  const finishPiLogin = useMutation({
    mutationFn: () => {
      if (!piLoginFlow) throw new Error('Sign in with ChatGPT first.')
      return connectionsApi.piLoginFinish({
        workspaceId,
        state: piLoginFlow.state,
        redirectUrl: piLoginRedirectUrl.trim(),
        ...(existing
          ? { connectionId: existing.id, ...(name.trim() ? { name: name.trim() } : {}) }
          : { name: name.trim() || CONNECTION_KIND_LABEL.pi_login }),
      })
    },
    onSuccess: onDone,
  })

  // A new ChatGPT connection always signs in; an existing one only does when
  // the user pasted a fresh sign-in address — otherwise Save just renames it.
  const reconnecting = isPiLoginOAuth && (!existing || piLoginRedirectUrl.trim() !== '')

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (reconnecting) {
      finishPiLogin.mutate()
    } else {
      save.mutate()
    }
  }

  const canSave = existing
    ? name.trim() !== ''
    : isApi
      ? baseUrl.trim() !== ''
      : token.trim().length >= 20

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      {!existing ? (
        <fieldset>
          <legend className="mb-2 text-base font-medium text-ink">
            What kind of login?
          </legend>
          <div className="space-y-2">
            {KINDS.map((option) => {
              const selected = option.kind === kind
              return (
                <label
                  key={option.kind}
                  className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                    selected
                      ? 'border-action bg-action/5'
                      : 'border-line hover:border-line-strong'
                  }`}
                >
                  <input
                    type="radio"
                    name="connection-kind"
                    className="mt-1"
                    checked={selected}
                    onChange={() => setKind(option.kind)}
                  />
                  <span>
                    <span className="block text-base font-medium text-ink">
                      {CONNECTION_KIND_LABEL[option.kind]}
                    </span>
                    <span className="block text-sm text-ink-3">
                      {option.text}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>
      ) : null}

      <Field
        label="Name"
        htmlFor="connection-name"
        hint="How agents and runs refer to it, e.g. “Paul’s Max account” or “OpenRouter”."
      >
        <input
          id="connection-name"
          className="field-input"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={
            isApi && selectedProvider
              ? selectedProvider.name
              : CONNECTION_KIND_LABEL[kind]
          }
        />
      </Field>

      {isApi ? (
        <>
          <Field label="Provider" htmlFor="connection-provider">
            <ProviderPicker
              id="connection-provider"
              providerId={providerId}
              onSelect={selectProvider}
            />
          </Field>
          {selectedProvider ? null : (
            <Field
              label="API address"
              htmlFor="connection-base-url"
              hint="The base URL, ending before /chat/completions, e.g. https://openrouter.ai/api/v1. It has to be reachable from the internet: runs can’t reach your own network."
            >
              <input
                id="connection-base-url"
                className="field-input font-mono"
                placeholder="https://…/v1"
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                autoCapitalize="none"
                spellCheck={false}
                required
              />
            </Field>
          )}
          <Field
            label="API key"
            htmlFor="connection-api-key"
            hint={
              existing
                ? 'Leave empty to keep the stored key.'
                : 'Leave empty if the API needs no key.'
            }
          >
            <input
              id="connection-api-key"
              type="password"
              className="field-input font-mono"
              placeholder={selectedProvider?.keyPlaceholder ?? 'sk-…'}
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            {selectedProvider ? (
              <a
                href={selectedProvider.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-block text-sm text-ink-3 underline"
              >
                Where to get a key ↗
              </a>
            ) : null}
          </Field>
        </>
      ) : isPiLoginOAuth ? (
        <div className="space-y-3 rounded-control border border-line p-4">
          <p className="text-sm text-ink-3">
            Sign in with your ChatGPT account. OpenAI sends you to a
            localhost page that won’t load — that’s expected; copy its
            address from the browser bar and paste it below.
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => startPiLogin.mutate()}
            disabled={startPiLogin.isPending}
          >
            {startPiLogin.isPending
              ? 'Getting a sign-in link…'
              : piLoginFlow
                ? 'Get a new sign-in link'
                : existing
                  ? 'Sign in with ChatGPT again'
                  : 'Sign in with ChatGPT'}
          </button>
          {startPiLogin.isError ? (
            <Notice tone="error">
              {errorText(startPiLogin.error, 'Couldn’t start the sign-in.')}
            </Notice>
          ) : null}
          {piLoginFlow ? (
            <>
              <p className="text-sm">
                <a
                  href={piLoginFlow.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-action underline"
                >
                  Open the OpenAI sign-in ↗
                </a>
              </p>
              <Field
                label="Paste the address you landed on"
                htmlFor="pi-login-redirect"
              >
                <input
                  id="pi-login-redirect"
                  className="field-input font-mono"
                  placeholder="http://localhost:…/callback?code=…&state=…"
                  value={piLoginRedirectUrl}
                  onChange={(event) => setPiLoginRedirectUrl(event.target.value)}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </Field>
            </>
          ) : null}
        </div>
      ) : help ? (
        <Field
          label={existing ? `New ${help.label.toLowerCase()}` : help.label}
          htmlFor="connection-token"
          hint={
            existing
              ? `Leave empty to keep the stored login. ${help.how}`
              : help.how
          }
        >
          <input
            id="connection-token"
            type="password"
            className="field-input font-mono"
            placeholder={help.placeholder}
            value={token}
            onChange={(event) => setToken(event.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
          />
        </Field>
      ) : null}

      {reconnecting && finishPiLogin.isError ? (
        <Notice tone="error">
          {errorText(finishPiLogin.error, 'Couldn’t finish the sign-in.')}
        </Notice>
      ) : null}
      {!reconnecting && save.isError ? (
        <Notice tone="error">
          {errorText(save.error, 'Couldn’t save the connection.')}
        </Notice>
      ) : null}

      <div className="flex justify-end border-t border-line pt-4">
        <button
          type="submit"
          className="btn-primary"
          disabled={
            reconnecting
              ? !piLoginFlow || !piLoginRedirectUrl.trim() || finishPiLogin.isPending
              : save.isPending || !canSave
          }
        >
          {reconnecting
            ? finishPiLogin.isPending
              ? 'Connecting…'
              : existing
                ? 'Save and reconnect'
                : 'Connect'
            : save.isPending
              ? isApi
                ? 'Checking the API…'
                : 'Saving…'
              : existing
                ? 'Save'
                : isApi
                  ? 'Check and add'
                  : 'Add connection'}
        </button>
      </div>
    </form>
  )
}

const MODEL_TIER_LABEL: Record<ModelTier, string> = {
  light: 'Light',
  standard: 'Standard',
  heavy: 'Heavy',
}

/**
 * The model each level (light, standard, heavy) stands for on this
 * connection. A decision step can set a level on a task; its later runs then
 * use the model of that level. An empty level falls back to the agent's own
 * model (Claude Code: haiku, sonnet and opus).
 */
function ModelTiersBlock({
  workspaceId,
  connection,
  onSaved,
}: {
  workspaceId: string
  connection: ExecutorConnection
  onSaved: () => void
}) {
  const [tiers, setTiers] = useState<ConnectionModelTiers>(
    connection.modelTiers ?? {}
  )
  // What is stored: the dialog's `connection` is a snapshot from when it opened.
  const [saved, setSaved] = useState<ConnectionModelTiers>(
    connection.modelTiers ?? {}
  )
  const isClaude = connection.kind === 'claude_cli'

  const modelsQuery = useQuery({
    queryKey: ['executor-models', `id:${connection.id}`, workspaceId],
    queryFn: () => connectionsApi.models({ connectionId: connection.id }, workspaceId),
    enabled: workspaceId !== '',
    staleTime: 5 * 60 * 1000,
  })
  const list = modelsQuery.data
  const models = list && list.source !== 'unavailable' ? list.models : []

  const save = useMutation({
    mutationFn: () => {
      const modelTiers: ConnectionModelTiers = {}
      for (const tier of MODEL_TIERS) {
        const value = tiers[tier]?.trim()
        if (value) modelTiers[tier] = value
      }
      return connectionsApi
        .update(connection.id, { workspaceId, modelTiers })
        .then(() => modelTiers)
    },
    onSuccess: (modelTiers) => {
      setSaved(modelTiers)
      onSaved()
    },
  })

  const dirty = MODEL_TIERS.some(
    (tier) => (tiers[tier] ?? '') !== (saved[tier] ?? '')
  )

  return (
    <form
      className="mt-5 space-y-3 border-t border-line pt-5"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div>
        <h3 className="text-base font-medium text-ink">Model levels</h3>
        <p className="text-sm text-ink-3">
          Which model a decision step’s level picks on this connection. Leave a
          level empty to keep the agent’s own model.
        </p>
      </div>
      {MODEL_TIERS.map((tier) => {
        const value = tiers[tier] ?? ''
        const id = `model-tier-${tier}`
        return (
          <Field key={tier} label={MODEL_TIER_LABEL[tier]} htmlFor={id}>
            <select
              id={id}
              className="field-input"
              value={value}
              onChange={(event) =>
                setTiers((current) => ({ ...current, [tier]: event.target.value }))
              }
            >
              <option value="">
                {isClaude
                  ? `Default (${CLAUDE_CLI_DEFAULT_MODEL_TIERS[tier]})`
                  : 'Not set'}
              </option>
              {/* A saved model the provider no longer lists stays selectable. */}
              {value && !models.some((m) => m.id === value) ? (
                <option value={value}>{value}</option>
              ) : null}
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name && m.name !== m.id ? `${m.name} (${m.id})` : m.id}
                </option>
              ))}
            </select>
          </Field>
        )
      })}
      {list?.message ? (
        <p className="text-sm text-ink-3">{list.message}</p>
      ) : null}
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, 'Couldn’t save the model levels.')}
        </Notice>
      ) : null}
      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-secondary"
          disabled={save.isPending || !dirty}
        >
          {save.isPending ? 'Saving…' : 'Save model levels'}
        </button>
      </div>
    </form>
  )
}
